// src/pages/clab-biodata-amended/index.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import SpinnerIcon from "components/ui/SpinnerIcon";
import { clabAPI, biodataAPI } from "utils/api";
import { useBiodataCache } from "context/BiodataCacheContext";
import PaginationComponent from "components/PaginationComponent";
import { useAuth } from "context/AuthContext";
import { getUserRoleForComponents } from "utils/authHelpers";
import FilterPanel from "./components/FilterPanel";
import AmendmentHistoryModal from "./components/AmendmentHistoryModal";
import RejectionModal from "../clab-biodata-pending/components/RejectionModal";
import BiodataDetailsView from "components/BiodataDetailsView";
import Notifier from "components/ui/Notifier";
import { useNotifications } from "hooks/useNotifications";

const ClabBiodataAmended = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [biodataList, setBiodataList] = useState([]);
  const [biodataPagination, setBiodataPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBiodata, setSelectedBiodata] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBiodataDetails, setSelectedBiodataDetails] = useState(null);
  const [showAmendmentHistoryModal, setShowAmendmentHistoryModal] =
    useState(false);
  const [amendmentHistoryData, setAmendmentHistoryData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    nationality: "",
    recruiter_id: "",
    date_from: "",
    date_to: "",
    skill: "",
  });
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTargetId, setApproveTargetId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const {
    notifications,
    showSuccess,
    showError,
    removeNotification: dismissNotification,
  } = useNotifications();
  const {
    getBiodataByStatus,
    invalidateOnUpdate,
    getPhotoUrl,
    prefetchPhotoUrl,
  } = useBiodataCache();

  const rejectionReasons = [
    "Expired medical certificate",
    "Data mismatch in documents",
    "Worker over age limit",
    "Incomplete documentation",
    "Invalid passport information",
    "Skill certificate expired",
    "Duplicate entry found",
    "Other",
  ];

  useEffect(() => {
    let isMounted = true;

    const loadAmendedBiodata = async () => {
      try {
        setLoading(true);
        setError(null);
        // Build query params from filters and currentPage
        const params = {
          page: currentPage,
          per_page: itemsPerPage,
          search: filters.search || undefined,
          nationality: filters.nationality || undefined,
          recruiter_id: filters.recruiter_id || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          skill: filters.skill || undefined,
        };
        const result = await getBiodataByStatus("resubmitted", params);

        if (result.fromCache) {
        }

        if (isMounted) {
          if (!result.success) {
            setBiodataList([]);
            setBiodataPagination(null);
            throw new Error(result?.message || "No amended biodata found");
          }
          const items = Array.isArray(result.records) ? result.records : [];
          setBiodataList(items);
          setBiodataPagination(result.pagination);
        }
      } catch (error) {
        if (isMounted) {
          setError("Failed to load amended biodata");
          console.error("Error loading amended biodata:", error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAmendedBiodata();

    return () => {
      isMounted = false;
    };
  }, [filters, currentPage, itemsPerPage]);

  // When filters change, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // When items per page changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Progressive photo prefetch when rows become visible
  useEffect(() => {
    const elements = document.querySelectorAll("[data-biodata-id]");
    if (!elements || elements.length === 0) return;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idAttr = entry.target.getAttribute("data-biodata-id");
          const id = Number(idAttr) || idAttr;
          if (!id || seen.has(id)) return;
          const rec = biodataList.find((b) => b.id === id);
          const direct = rec?.photo_url || rec?.primary_photo_url;
          const cached = getPhotoUrl(id, rec);
          if (!direct && !cached) prefetchPhotoUrl(id);
          seen.add(id);
          observer.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.25 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [biodataList, getPhotoUrl, prefetchPhotoUrl]);

  // Redirect if not CLAB user
  useEffect(() => {
    if (
      userProfile &&
      !["clab_user", "clab", "admin", "master_admin", "1", 1].includes(
        userProfile.role
      )
    ) {
      navigate("/login-screen");
    }
  }, [userProfile, navigate]);

  // Use backend pagination
  const paginatedBiodata = biodataList;
  const totalPages = biodataPagination ? biodataPagination.total_pages : 1;
  // Pagination controls

  const handleSelectBiodata = (biodataId) => {
    setSelectedBiodata((prev) =>
      prev.includes(biodataId)
        ? prev.filter((id) => id !== biodataId)
        : [...prev, biodataId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBiodata.length === paginatedBiodata.length) {
      setSelectedBiodata([]);
    } else {
      setSelectedBiodata(paginatedBiodata.map((b) => b.id));
    }
  };

  const handleViewDetails = async (biodata) => {
    try {
      setError(null);
      const result = await biodataAPI.getBiodataById(biodata.id);
      if (result?.status !== "success" || !result?.data) {
        throw new Error(result?.message || "Unable to fetch biodata details");
      }
      const details = result.data;
      setSelectedBiodataDetails({
        ...details,
        recruiter: details.recruiter || biodata.recruiter || null,
        amendment_summary: biodata.amendment_summary,
        total_amendment_cycles: biodata.total_amendment_cycles,
        latest_rejection: biodata.latest_rejection,
      });
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Failed to load biodata details:", error);
      showError("Failed to load biodata details. Please try again.");
    }
  };

  const handleViewAmendmentHistory = async (biodataId) => {
    try {
      setError(null);
      const result = await clabAPI.getAmendmentHistory(biodataId);
      if (result?.status === "success" && result?.data) {
        setAmendmentHistoryData(result.data);
        setShowAmendmentHistoryModal(true);
      } else {
        throw new Error(result?.message || "Unable to fetch amendment history");
      }
    } catch (error) {
      console.error("Failed to load amendment history:", error);
      showError("Failed to load amendment history. Please try again.");
    }
  };

  const handleApprove = async (biodataId) => {
    setApproveTargetId(biodataId);
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    setIsUpdating(true); // Set loading only after user confirms
    try {
      await clabAPI.approveBiodata(approveTargetId);
      invalidateOnUpdate([approveTargetId], "resubmitted", "approved");
      setBiodataList((prev) => prev.filter((b) => b.id !== approveTargetId));
      setSelectedBiodata((prev) => prev.filter((id) => id !== approveTargetId));
      showSuccess("The amended biodata has been approved successfully");
      setShowDetailsModal(false);
      setSelectedBiodataDetails(null);
    } catch (error) {
      showError("Failed to approve amended biodata");
    } finally {
      setShowApproveModal(false);
      setApproveTargetId(null);
      setIsUpdating(false);
    }
  };

  const handleReject = async (biodataId, rejectionData) => {
    setIsUpdating(true);
    try {
      await clabAPI.rejectBiodata(biodataId, rejectionData);
      invalidateOnUpdate([biodataId], "resubmitted", "rejected");
      setBiodataList((prev) => prev.filter((b) => b.id !== biodataId));
      setSelectedBiodata((prev) => prev.filter((id) => id !== biodataId));
      showSuccess("The amended biodata has been rejected");
      setShowDetailsModal(false);
      setSelectedBiodataDetails(null);
    } catch (error) {
      showError("Failed to reject amended biodata");
    } finally {
      setShowRejectModal(false);
      setRejectTargetId(null);
      setIsUpdating(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  if (loading && !biodataList.length) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar
          userRole={userRole}
          userName={userProfile?.full_name || "CLAB User"}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <RoleBasedSidebar
          userRole={userRole}
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main
          className={`transition-all duration-300 pt-16 ${
            sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
        >
          <div className="p-6">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <SpinnerIcon size="large" />
                <p className="mt-4 text-text-secondary">
                  Loading amended biodata...
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole={userRole}
        userName={userProfile?.full_name || "CLAB User"}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <RoleBasedSidebar
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6 space-y-6">
          {/* Breadcrumb */}
          <BreadcrumbNavigation
            items={[
              { label: "Dashboard", path: "/clab-dashboard" },
              {
                label: "Amended Biodata Review",
                path: "/clab-biodata-amended",
              },
            ]}
          />

          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                Amended Biodata Review
              </h1>
              <p className="text-text-secondary mt-1">
                Review biodata that has been amended after rejection
              </p>
            </div>
            <div className="bg-info-50 border border-info-200 rounded-lg p-3">
              <p className="text-sm text-info-700">
                <Icon name="info" size={16} className="inline mr-2" />
                Total Amended: {biodataPagination?.total || 0}
              </p>
            </div>
          </div>

          {/* Filters */}
          <FilterPanel onFilterChange={handleFilterChange} />

          {/* Error Display */}
          {error && (
            <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
              <div className="flex items-center">
                <Icon
                  name="alert-circle"
                  size={20}
                  className="text-danger-600 mr-3"
                />
                <p className="text-danger-700">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-danger-600 hover:text-danger-800"
                >
                  <Icon name="x" size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Success Notifier */}
          {notifications.length > 0 && (
            <Notifier
              type={notifications[notifications.length - 1].type}
              message={notifications[notifications.length - 1].message}
              show={notifications.length > 0}
              onClose={() =>
                dismissNotification(notifications[notifications.length - 1]?.id)
              }
              duration={
                notifications.length > 0
                  ? notifications[notifications.length - 1].duration
                  : 3000
              }
            />
          )}

          {/* Biodata Table */}
          <div className="bg-surface rounded-lg border border-border">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-text-primary">
                  Amended Biodata List
                </h2>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-text-secondary">
                      {selectedBiodata.length} of {paginatedBiodata.length}{" "}
                      selected
                    </span>
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-primary hover:text-primary-dark"
                    >
                      {selectedBiodata.length === paginatedBiodata.length
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <SpinnerIcon size="medium" />
                <p className="mt-2 text-text-secondary">Loading...</p>
              </div>
            ) : paginatedBiodata.length === 0 ? (
              <div className="p-8 text-center">
                <Icon
                  name="folder-open"
                  size={48}
                  className="text-text-tertiary mx-auto mb-4"
                />
                <h3 className="text-lg font-medium text-text-secondary mb-2">
                  No Amended Biodata Found
                </h3>
                <p className="text-text-tertiary">
                  {Object.values(filters).some((f) => f)
                    ? "Try adjusting your filters to see more results."
                    : "No biodata has been amended and resubmitted yet."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-background border-b border-border">
                    <tr>
                      <th className="w-12 px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={
                            selectedBiodata.length ===
                              paginatedBiodata.length &&
                            paginatedBiodata.length > 0
                          }
                          onChange={handleSelectAll}
                          className="rounded border-border"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                        Photo
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                        Worker Details
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                        Skills
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                        Amendment Info
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                        Latest Rejection
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                        Recruiter
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                        Last Updated
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-text-secondary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBiodata.map((biodata) => (
                      <tr
                        key={biodata.id}
                        className="border-b border-border hover:bg-background/50"
                      >
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedBiodata.includes(biodata.id)}
                            onChange={() => handleSelectBiodata(biodata.id)}
                            className="rounded border-border"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div
                            className="w-12 h-12 rounded-lg overflow-hidden bg-background border border-border"
                            data-biodata-id={biodata.id}
                          >
                            <Image
                              src={getPhotoUrl(biodata.id, biodata)}
                              alt={biodata.full_name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-text-primary">
                              {biodata.full_name}
                            </p>
                            <p className="text-sm text-text-secondary">
                              Passport: {biodata.passport_no}
                            </p>
                            <p className="text-sm text-text-secondary">
                              Nationality: {biodata.nationality}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-text-secondary">
                            {Array.isArray(biodata.skills)
                              ? biodata.skills.join(", ")
                              : biodata.skills || biodata.skill || "-"}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <span className="px-2 py-1 bg-warning-100 text-warning-800 text-xs rounded">
                                Cycle {biodata.total_amendment_cycles || 1}
                              </span>
                              <StatusIndicatorBadge status="amended" />
                            </div>
                            {biodata.amendment_summary &&
                              biodata.amendment_summary.length > 0 && (
                                <p className="text-xs text-text-tertiary">
                                  {biodata.amendment_summary[0].total_changes}{" "}
                                  changes made
                                </p>
                              )}
                            <button
                              onClick={() =>
                                handleViewAmendmentHistory(biodata.id)
                              }
                              className="text-xs text-primary hover:text-primary-dark underline"
                            >
                              View Amendment History
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {biodata.latest_rejection ? (
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-error-600">
                                {biodata.latest_rejection.reason ||
                                  "Reason not provided"}
                              </p>
                              {biodata.latest_rejection.remarks && (
                                <p className="text-xs text-text-secondary">
                                  {biodata.latest_rejection.remarks}
                                </p>
                              )}
                              {biodata.latest_rejection.rejected_at && (
                                <p className="text-xs text-text-tertiary">
                                  {new Date(
                                    biodata.latest_rejection.rejected_at
                                  ).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-text-tertiary">
                              No rejection reason
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-sm font-medium text-text-primary">
                              {biodata.recruiter?.company_name ||
                                biodata.recruiter?.name ||
                                "Unknown"}
                            </p>
                            <p className="text-xs text-text-secondary">
                              {biodata.recruiter?.contact_person || "N/A"}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-text-secondary">
                            {new Date(biodata.updated_at).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-text-tertiary">
                            {new Date(biodata.updated_at).toLocaleTimeString()}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            {/* View Details */}
                            <button
                              onClick={() => handleViewDetails(biodata)}
                              disabled={isUpdating}
                              className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150 disabled:opacity-50"
                              title="View Details"
                            >
                              <Icon name="Eye" size={16} />
                            </button>

                            {/* Approve */}
                            <button
                              onClick={() => handleApprove(biodata.id)}
                              disabled={isUpdating}
                              className="p-2 text-success hover:bg-success-50 rounded-md transition-colors duration-150 disabled:opacity-50"
                              title="Approve Amendment"
                            >
                              {isUpdating && approveTargetId === biodata.id ? (
                                <Icon
                                  name="Loader2"
                                  className="animate-spin"
                                  size={16}
                                />
                              ) : (
                                <Icon name="CheckCircle" size={16} />
                              )}
                            </button>

                            {/* Reject */}
                            <button
                              onClick={() => {
                                setRejectTargetId(biodata.id);
                                setShowRejectModal(true);
                              }}
                              disabled={isUpdating}
                              className="p-2 text-error hover:bg-error-50 rounded-md transition-colors duration-150 disabled:opacity-50"
                              title="Reject Amendment"
                            >
                              {isUpdating && rejectTargetId === biodata.id ? (
                                <Icon
                                  name="Loader2"
                                  className="animate-spin"
                                  size={16}
                                />
                              ) : (
                                <Icon name="XCircle" size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Component */}
            {biodataPagination && (
              <div className="px-6 py-4 border-t border-secondary-200">
                <PaginationComponent
                  pagination={{
                    currentPage: biodataPagination.current_page || currentPage,
                    perPage: biodataPagination.per_page || itemsPerPage,
                    total: biodataPagination.total || 0,
                    totalPages: biodataPagination.total_pages || totalPages,
                    from:
                      biodataPagination.from ||
                      (currentPage - 1) * itemsPerPage + 1,
                    to:
                      biodataPagination.to ||
                      Math.min(
                        currentPage * itemsPerPage,
                        biodataPagination.total || 0
                      ),
                  }}
                  onPageChange={setCurrentPage}
                  onPerPageChange={setItemsPerPage}
                  showPerPageSelector={true}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Biodata Details Modal */}
      {showDetailsModal && selectedBiodataDetails && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="biodata-modal-overlay fixed inset-0 z-[1100] bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
            <div className="biodata-modal-content bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
                <h2 className="text-xl font-bold">Amended Biodata Details</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedBiodataDetails(null);
                  }}
                  className="text-secondary-600 hover:text-secondary-900"
                >
                  Close
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <BiodataDetailsView
                  biodata={selectedBiodataDetails}
                  title="Amended Biodata Overview"
                />
              </div>
              <div className="p-6 border-t border-secondary-200 flex items-center justify-end gap-3">
                <button
                  onClick={() =>
                    handleViewAmendmentHistory(selectedBiodataDetails.id)
                  }
                  className="px-4 py-2 bg-info-100 text-info-800 rounded"
                >
                  View Amendment History
                </button>
                <button
                  onClick={() => {
                    setRejectTargetId(selectedBiodataDetails.id);
                    setShowRejectModal(true);
                  }}
                  className="px-4 py-2 bg-error text-white rounded"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(selectedBiodataDetails.id)}
                  className="px-4 py-2 bg-success text-white rounded"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Amendment History Modal */}
      {showAmendmentHistoryModal && amendmentHistoryData && (
        <AmendmentHistoryModal
          data={amendmentHistoryData}
          onClose={() => {
            setShowAmendmentHistoryModal(false);
            setAmendmentHistoryData(null);
          }}
        />
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1200]">
          <div className="bg-surface rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              Confirm Approval
            </h3>
            <p className="text-text-secondary mb-6">
              Are you sure you want to approve this amended biodata? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setApproveTargetId(null);
                  setIsUpdating(false);
                }}
                disabled={isUpdating}
                className="px-4 py-2 bg-secondary-100 rounded disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                disabled={isUpdating}
                className="px-4 py-2 bg-success text-white rounded disabled:opacity-50 flex items-center space-x-2"
              >
                {isUpdating && <SpinnerIcon size={16} />}
                <span>Approve</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <RejectionModal
          type="single"
          biodataIds={rejectTargetId}
          rejectionReasons={rejectionReasons}
          onClose={() => {
            setShowRejectModal(false);
            setRejectTargetId(null);
            setIsUpdating(false);
          }}
          onConfirm={(reasonObj) => handleReject(rejectTargetId, reasonObj)}
        />
      )}
    </div>
  );
};

export default ClabBiodataAmended;
